import React, { useEffect, useState } from 'react';
import { Loader2, Save, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { expensasApi } from '../../../lib/api';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';

export default function TabParametros({ edificioId, registerHeader }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [original, setOriginal] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await expensasApi.getConfig(edificioId);
        setCfg(r.data);
        setOriginal(JSON.parse(JSON.stringify(r.data)));
      } finally { setLoading(false); }
    })();
  }, [edificioId]);

  const handleCancel = () => {
    setCfg(JSON.parse(JSON.stringify(original)));
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const modo = cfg.responsable_modo || 'contrato';
      const payload = {
        porc_fondo_reserva: Number(cfg.porc_fondo_reserva || 0),
        porc_recargo_vto2: Number(cfg.porc_recargo_vto2 || 0),
        porc_recargo_vto3: Number(cfg.porc_recargo_vto3 || 0),
        dia_vto1: Number(cfg.dia_vto1 || 10),
        dia_vto2: Number(cfg.dia_vto2 || 20),
        dia_vto3: Number(cfg.dia_vto3 || 31),
        responsable_modo: modo,
        responsable_fijo: modo === 'fijo' ? {
          razon_social: (cfg.responsable_fijo?.razon_social || '').trim(),
          cuit: (cfg.responsable_fijo?.cuit || '').trim() || null,
          email: (cfg.responsable_fijo?.email || '').trim() || null,
        } : null,
      };
      if (modo === 'fijo' && !payload.responsable_fijo.razon_social) {
        toast.error('La razón social del responsable fijo es obligatoria');
        setSaving(false);
        return;
      }
      const r = await expensasApi.upsertConfig(edificioId, payload);
      setCfg(r.data);
      setOriginal(JSON.parse(JSON.stringify(r.data)));
      setEditing(false);
      toast.success('Configuración guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!registerHeader) return;
    registerHeader(
      editing ? (
        <>
          <Button size="sm" variant="outline" onClick={handleCancel} disabled={saving} data-testid="cancelar-cfg-btn">
            <X className="w-4 h-4 mr-1" /> Cancelar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !cfg} data-testid="guardar-cfg-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Guardar
          </Button>
        </>
      ) : (
        <Button size="sm" onClick={() => setEditing(true)} disabled={!cfg} data-testid="editar-cfg-btn">
          <Pencil className="w-4 h-4 mr-1" /> Editar
        </Button>
      )
    );
    return () => registerHeader(null);
    // eslint-disable-next-line
  }, [cfg, saving, editing]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!cfg) return null;

  const dis = !editing;

  return (
    <div className="max-w-3xl space-y-6">
      <section>
        <h3 className="font-semibold text-slate-900 mb-3">Fondo de reserva</h3>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <Label>Porcentaje (%)</Label>
            <Input type="number" step="0.01" value={cfg.porc_fondo_reserva ?? ''} disabled={dis}
                   onChange={(e) => setCfg({ ...cfg, porc_fondo_reserva: e.target.value })}
                   data-testid="cfg-pfr" />
            <p className="text-[11px] text-slate-500 mt-1">Aplica fila por fila sobre las 3 columnas anteriores.</p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-semibold text-slate-900 mb-3">Vencimientos del recibo (mes siguiente al período)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Vencimiento 1 - día</Label>
            <Input type="number" min={1} max={31} value={cfg.dia_vto1 ?? ''} disabled={dis}
                   onChange={(e) => setCfg({ ...cfg, dia_vto1: e.target.value })} />
            <p className="text-[11px] text-slate-500 mt-1">Sin recargo</p>
          </div>
          <div>
            <Label>Vencimiento 2 - día</Label>
            <Input type="number" min={1} max={31} value={cfg.dia_vto2 ?? ''} disabled={dis}
                   onChange={(e) => setCfg({ ...cfg, dia_vto2: e.target.value })} />
            <Input type="number" step="0.1" className="mt-1" placeholder="Recargo %" disabled={dis}
                   value={cfg.porc_recargo_vto2 ?? ''}
                   onChange={(e) => setCfg({ ...cfg, porc_recargo_vto2: e.target.value })} />
          </div>
          <div>
            <Label>Vencimiento 3 - día</Label>
            <Input type="number" min={1} max={31} value={cfg.dia_vto3 ?? ''} disabled={dis}
                   onChange={(e) => setCfg({ ...cfg, dia_vto3: e.target.value })} />
            <Input type="number" step="0.1" className="mt-1" placeholder="Recargo %" disabled={dis}
                   value={cfg.porc_recargo_vto3 ?? ''}
                   onChange={(e) => setCfg({ ...cfg, porc_recargo_vto3: e.target.value })} />
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-semibold text-slate-900 mb-3">Responsable de pagar las expensas</h3>
        <p className="text-xs text-slate-500 mb-3">
          Si la unidad está vacante, el responsable es siempre el propietario.
          Si está alquilada, definí cómo se resuelve:
        </p>
        <div className="space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="responsable-modo"
              value="contrato"
              checked={(cfg.responsable_modo || 'contrato') === 'contrato'}
              disabled={dis}
              onChange={() => setCfg({ ...cfg, responsable_modo: 'contrato', responsable_fijo: null })}
              data-testid="resp-modo-contrato"
              className="mt-1"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">Inquilino del contrato vigente</div>
              <div className="text-xs text-slate-500">Caso típico (Foresta). Cuando hay contrato, paga el inquilino real del alquiler.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="responsable-modo"
              value="fijo"
              checked={cfg.responsable_modo === 'fijo'}
              disabled={dis}
              onChange={() => setCfg({
                ...cfg,
                responsable_modo: 'fijo',
                responsable_fijo: cfg.responsable_fijo || { razon_social: '', cuit: '', email: '' },
              })}
              data-testid="resp-modo-fijo"
              className="mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">Responsable fijo (administración Full)</div>
              <div className="text-xs text-slate-500 mb-2">Caso Torre Emilia. Siempre paga la misma entidad (ej: Global Citizen SAS) sin importar quién sea el inquilino real.</div>
              {cfg.responsable_modo === 'fijo' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-xl mt-2">
                  <div>
                    <Label className="text-xs">Razón social *</Label>
                    <Input
                      type="text"
                      value={cfg.responsable_fijo?.razon_social ?? ''}
                      disabled={dis}
                      onChange={(e) => setCfg({ ...cfg, responsable_fijo: { ...(cfg.responsable_fijo || {}), razon_social: e.target.value } })}
                      data-testid="resp-fijo-rs"
                      placeholder="Global Citizen SAS"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CUIT</Label>
                    <Input
                      type="text"
                      value={cfg.responsable_fijo?.cuit ?? ''}
                      disabled={dis}
                      onChange={(e) => setCfg({ ...cfg, responsable_fijo: { ...(cfg.responsable_fijo || {}), cuit: e.target.value } })}
                      data-testid="resp-fijo-cuit"
                      placeholder="30-71604248-7"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={cfg.responsable_fijo?.email ?? ''}
                      disabled={dis}
                      onChange={(e) => setCfg({ ...cfg, responsable_fijo: { ...(cfg.responsable_fijo || {}), email: e.target.value } })}
                      data-testid="resp-fijo-email"
                      placeholder="administracion@gcitizen.com.ar"
                    />
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>
      </section>
    </div>
  );
}
